/**
 * NPC M3: 12 người — 3 nhà cung cấp, 6 khách quen, 3 hàng xóm.
 *
 * - Khách quen có thân thiết 1–5: tặng quà (tốn 1 món quà ưa thích trong kho)
 *   để tăng; đủ số quà (quaMoiCapThanThiet × cấp hiện tại) thì lên cấp,
 *   thưởng M18 XP mỗi cấp.
 * - Hội thoại có điều kiện: mức thân thiết tối thiểu + khung giờ trong ngày.
 * - Nhà cung cấp / hàng xóm không có thân thiết (tặng quà bị từ chối).
 */
import { congXp } from './xp.js';
import { layThamSoMoPhong } from './du-lieu.js';
import { gioTrongNgay } from './khach.js';
import type {
  DuLieuGame,
  HoiThoai,
  NoiDungNpc,
  NpcThamSo,
  TrangThaiGame,
  VaiTroNpc,
} from './types.js';

/** Tìm tham số NPC theo mã. Ném lỗi nếu mã lạ. */
export function layNpc(duLieu: DuLieuGame, ma: string): NpcThamSo {
  const npc = duLieu.npc.npc.find((n) => n.ma === ma);
  if (!npc) throw new Error(`Mã NPC lạ: ${ma}.`);
  return npc;
}

/** Nội dung chữ (tên, mô tả, câu chào) của NPC. */
export function layNoiDungNpc(duLieu: DuLieuGame, ma: string): NoiDungNpc {
  const nd = duLieu.noiDungNpc.find((n) => n.ma === ma);
  if (!nd) throw new Error(`Thiếu nội dung NPC ${ma} trong src/data/noi-dung/npc.json.`);
  return nd;
}

/** Các NPC đã mở theo cấp người chơi. */
export function npcDaMo(state: TrangThaiGame, duLieu: DuLieuGame): NpcThamSo[] {
  return duLieu.npc.npc.filter((n) => state.nguoiChoi.cap >= n.capNguoiChoiMo);
}

/** NPC theo vai trò (đã mở). */
export function npcTheoVaiTro(
  state: TrangThaiGame,
  duLieu: DuLieuGame,
  vaiTro: VaiTroNpc,
): NpcThamSo[] {
  return npcDaMo(state, duLieu).filter((n) => n.vaiTro === vaiTro);
}

/** Cấp thân thiết hiện tại (mặc định 1 khi mới quen). */
export function capThanThiet(state: TrangThaiGame, ma: string): number {
  return state.npcThanThiet[ma] ?? 1;
}

/** Số quà cần để lên cấp thân thiết tiếp theo (từ cấp hiện tại). */
export function quaCanDeLenCap(state: TrangThaiGame, duLieu: DuLieuGame, ma: string): number {
  const npc = layNpc(duLieu, ma);
  if (npc.quaMoiCapThanThiet <= 0) return 0;
  return npc.quaMoiCapThanThiet * capThanThiet(state, ma);
}

export interface KetQuaTangQua {
  lenCap: boolean;
  capMoi: number;
}

/**
 * Tặng quà cho khách quen: tốn 1 món quà ưa thích trong kho.
 * Ném lỗi tiếng Việt nếu không tặng được (không phải khách quen, đã max,
 * hết quà trong kho).
 */
export function tangQua(state: TrangThaiGame, duLieu: DuLieuGame, ma: string): KetQuaTangQua {
  const npc = layNpc(duLieu, ma);
  if (npc.vaiTro !== 'khachQuen' || npc.quaMoiCapThanThiet <= 0) {
    throw new Error(`${layNoiDungNpc(duLieu, ma).ten} không nhận quà theo kiểu thân thiết.`);
  }
  const cap = capThanThiet(state, ma);
  if (cap >= 5) {
    throw new Error('Thân thiết đã đạt tối đa (5).');
  }
  const maQua = npc.quaUaThich;
  const ton = state.kho[maQua]?.ton ?? 0;
  if (ton < 1) {
    const tenQua = duLieu.matHang.find((m) => m.ma === maQua)?.ten ?? maQua;
    throw new Error(`Hết ${tenQua} trong kho để làm quà.`);
  }
  state.kho[maQua]!.ton -= 1;
  state.thongKe.soQuaTang += 1;
  const daTang = (state.npcQuaDaTang[ma] ?? 0) + 1;
  state.npcQuaDaTang[ma] = daTang;
  if (daTang >= quaCanDeLenCap(state, duLieu, ma)) {
    state.npcThanThiet[ma] = cap + 1;
    state.npcQuaDaTang[ma] = 0;
    congXp(state, duLieu, layThamSoMoPhong(duLieu, 'M18'));
    return { lenCap: true, capMoi: cap + 1 };
  }
  return { lenCap: false, capMoi: cap };
}

/**
 * Các câu thoại khả dụng của NPC tại thời điểm hiện tại:
 * NPC đã mở + đạt thân thiết tối thiểu + trong khung giờ (nếu có).
 */
export function hoiThoaiKhaDung(state: TrangThaiGame, duLieu: DuLieuGame, ma: string): HoiThoai[] {
  const npc = layNpc(duLieu, ma);
  if (state.nguoiChoi.cap < npc.capNguoiChoiMo) return [];
  const gio = gioTrongNgay(state.tongGioGame);
  const cap = capThanThiet(state, ma);
  return duLieu.hoiThoai.filter((h) => {
    if (h.npc !== ma) return false;
    const dk = h.dieuKien;
    if ((dk.thanThietToiThieu ?? 1) > cap) return false;
    if (dk.gioBatDau !== undefined && dk.gioKetThuc !== undefined) {
      if (gio < dk.gioBatDau || gio >= dk.gioKetThuc) return false;
    }
    return true;
  });
}
